import { BaseEntity, getCachedRegionEntity, getCachedSolarSystemEntity } from ".";
import moment from "moment";
//import { CorporationsProjectsDetail } from "../service";
import { CorporationsProjectsDetail } from "../client/types.gen";

import { getCachedTypeEntity } from "./TypeEntityCache";
import { getCachedGroupEntity } from "./GroupEntityCache";
import { getCachedCharacterEntity } from "./CharacterEntityCache";
import { getCachedAllianceEntity } from "./AllianceEntityCache";
import { getCachedCorporationEntity } from "./CorporationEntityCache";

export class CorporationProjectDetailsEntity extends BaseEntity {
  constructor(id: number, props: any) {
    super(id, props);
    this._dataTypeName = "Corporation Project Details";
  }

  async toJson(): Promise<Excel.EntityCellValue> {
    const props: Partial<CorporationsProjectsDetail> = this.props;
    var entity = await super.toJson();
    // Properties
    if (entity.properties) {
      if (props.id) {
        entity.properties["project_id"] = {
          type: Excel.CellValueType.string,
          basicValue: props.id,
        };
      }
      let name = "Unknown Project Name";
      if (props.name) {
        name = props.name.toString();
      }
      if (props.last_modified) {
        const last_modified = moment(props.last_modified);
        entity.properties["last_modified"] = {
          type: Excel.CellValueType.formattedNumber,
          basicValue: last_modified.unix() / 86400 + 25569,
          numberFormat: "[$-409]m/d/yy h:mm AM/PM;@",
        };
      }

      if (props.details?.created) {
        const created = moment(props.details?.created);
        entity.properties["created"] = {
          type: Excel.CellValueType.formattedNumber,
          basicValue: created.unix() / 86400 + 25569,
          numberFormat: "[$-409]m/d/yy h:mm AM/PM;@",
        };
      }
      entity.properties["current_progress"] = {
        type: Excel.CellValueType.double,
        basicValue: props.progress?.current ?? 0, //progress.current
      };

      entity.properties["reward_remaining"] = {
        type: Excel.CellValueType.double,
        basicValue: props.reward?.remaining ?? 0,
      };
      entity.properties["reward_initial"] = {
        type: Excel.CellValueType.double,
        basicValue: props.reward?.initial ?? 0,
      };

      if (props.state) {
        entity.properties["state"] = {
          type: Excel.CellValueType.string,
          basicValue: props.state,
        };
      }
      entity.properties["creator"] = {
        type: Excel.CellValueType.string,
        basicValue: props.creator?.name ?? "Unknown",
      };
      if (props.contribution) {
        //TODO CREATE JSON Object nested

        entity.properties["contribution_participation_limit"] = {
          type: Excel.CellValueType.double,
          basicValue: props.contribution.participation_limit ?? 0,
        };

        entity.properties["contribution_reward_per_contribution"] = {
          type: Excel.CellValueType.double,
          basicValue: props.contribution.reward_per_contribution ?? 0,
        };

        entity.properties["contribution_submission_limit"] = {
          type: Excel.CellValueType.double,
          basicValue: props.contribution.submission_limit ?? 0,
        };

        entity.properties["contribution_submission_multiplier"] = {
          type: Excel.CellValueType.double,
          basicValue: props.contribution.submission_multiplier ?? 0,
        };
        //CONTRIBUTION ENDS
      }

      entity.properties["desired_progress"] = {
        type: Excel.CellValueType.double,
        basicValue: props.progress?.desired ?? 0, //progress.desired
      };

      entity.properties["career"] = {
        type: Excel.CellValueType.string,
        basicValue: props.details?.career ?? "Unknown",
      };
      //CONFIGURATION OBJECTS
      if (props.configuration) {
        // eslint-disable-next-line no-undef
        const configInfo = extractConfigurationValues(props.configuration);
        await processConfigurationToExcel(configInfo, entity);
      }
      entity.text = name;
      entity.properties["name"] = {
        type: Excel.CellValueType.string,
        basicValue: entity.text,
      };
      // eslint-disable-next-line no-undef
      //console.log("res", entity);
    }
    // Layouts
    entity.layouts = {
      card: {
        //title: { property: "name" },
      },
    };

    return entity;
  }
}

// Helper function to convert arrays to Excel entity arrays
async function createExcelEntityArray(
  items: any[],
  entityName: string,
  getResolvedEntity?: (item: any) => Promise<any>
): Promise<Excel.CellValue[][]> {
  const array: Excel.CellValue[][] = [];

  if (!items || !items.length) {
    return array;
  }

  for (const item of items) {
    const properties: { [key: string]: Excel.CellValue } = {};

    // Process all properties of the item
    for (const [key, value] of Object.entries(item)) {
      if (value !== null && value !== undefined) {
        if (typeof value === "number") {
          properties[key] = {
            type: Excel.CellValueType.formattedNumber,
            basicValue: value,
            numberFormat: "##0",
          };
        } else if (typeof value === "boolean") {
          properties[key] = {
            type: Excel.CellValueType.boolean,
            basicValue: value,
          };
        } else if (typeof value === "string") {
          properties[key] = {
            type: Excel.CellValueType.string,
            basicValue: value,
          };
        } else if (typeof value === "object" && value instanceof Date) {
          const dateValue = moment(value);
          properties[key] = {
            type: Excel.CellValueType.formattedNumber,
            basicValue: dateValue.unix() / 86400 + 25569,
            numberFormat: "[$-409]m/d/yy h:mm AM/PM;@",
          };
        } else {
          // For complex objects, store as JSON string
          properties[key] = {
            type: Excel.CellValueType.string,
            basicValue: JSON.stringify(value),
          };
        }
      }
    }

    // If there's a resolver function for getting additional entity data
    let resolvedEntity = null;
    if (getResolvedEntity) {
      resolvedEntity = await getResolvedEntity(item);
      if (resolvedEntity) {
        properties["resolved_entity"] = resolvedEntity;
      }
    }

    // Create entity with descriptive text
    let name;
    if (item.type_id) {
      const res = await getCachedTypeEntity(item.type_id);
      if (res?.props.name) name = res?.props.name;
    } else if (item.solar_system_id) {
      const res = await getCachedSolarSystemEntity(item.solar_system_id);
      if (res?.props.name.en) name = res?.props.name.en;
    } else if (item.group_id) {
      const res = await getCachedGroupEntity(item.group_id);
      if (res?.props.groupName) name = res?.props.groupName;
    } else if (item.character_id) {
      const res = await getCachedCharacterEntity(item.character_id);
      if (res?.props.name) name = res?.props.name;
    } else if (item.alliance_id) {
      const res = await getCachedAllianceEntity(item.alliance_id);
      if (res?.props.name) name = res?.props.name;
    } else if (item.corporation_id) {
      const res = await getCachedCorporationEntity(item.corporation_id);
      if (res?.props.name) name = res?.props.name;
    } else if (item.region_id) {
      const res = await getCachedRegionEntity(item.region_id);
      if (res?.props.name.en) name = res?.props.name.en;
    } else if (item.constellation_id) {
      name = "Constellation #" + item.constellation_id;
    }
    let entityText;
    if (name) {
      entityText = name;
    } else {
      entityText = item.type_id
        ? `${entityName} #${item.type_id}`
        : `${entityName} #${Object.values(item)[0] || "Unknown"}`;
    }

    array.push([
      {
        type: Excel.CellValueType.entity,
        text: entityText,
        properties: properties,
      },
    ]);
  }

  return array;
}

// Helper function to process configuration data into Excel properties
async function processConfigurationToExcel(configInfo: any, entity: any) {
  if (!configInfo.data || typeof configInfo.data !== "object") {
    return;
  }

  // Add configuration type
  entity.properties["configuration_type"] = {
    type: Excel.CellValueType.string,
    basicValue: configInfo.type,
  };

  // Process each property in the configuration data
  for (const [key, value] of Object.entries(configInfo.data)) {
    const propertyKey = `config_${key}`;

    if (Array.isArray(value)) {
      // Handle arrays by creating Excel entity arrays
      if (value.length > 0) {
        const entityArray = await createExcelEntityArray(value, key.slice(0, -1)); // Remove 's' from plural
        entity.properties[propertyKey] = {
          type: Excel.CellValueType.array,
          elements: entityArray,
        };
      }
    } else if (value !== null && value !== undefined) {
      // Handle primitive values
      if (typeof value === "number") {
        entity.properties[propertyKey] = {
          type: Excel.CellValueType.formattedNumber,
          basicValue: value,
          numberFormat: "##0",
        };
      } else if (typeof value === "boolean") {
        entity.properties[propertyKey] = {
          type: Excel.CellValueType.boolean,
          basicValue: value,
        };
      } else if (typeof value === "string") {
        entity.properties[propertyKey] = {
          type: Excel.CellValueType.string,
          basicValue: value,
        };
      } else if (typeof value === "object" && value instanceof Date) {
        const dateValue = moment(value);
        entity.properties[propertyKey] = {
          type: Excel.CellValueType.formattedNumber,
          basicValue: dateValue.unix() / 86400 + 25569,
          numberFormat: "[$-409]m/d/yy h:mm AM/PM;@",
        };
      } else if (typeof value === "object") {
        // For nested objects, recursively process them
        for (const [nestedKey, nestedValue] of Object.entries(value)) {
          const nestedPropertyKey = `${propertyKey}_${nestedKey}`;
          if (typeof nestedValue === "number") {
            entity.properties[nestedPropertyKey] = {
              type: Excel.CellValueType.formattedNumber,
              basicValue: nestedValue,
              numberFormat: "##0",
            };
          } else if (typeof nestedValue === "string") {
            entity.properties[nestedPropertyKey] = {
              type: Excel.CellValueType.string,
              basicValue: nestedValue,
            };
          } else if (typeof nestedValue === "boolean") {
            entity.properties[nestedPropertyKey] = {
              type: Excel.CellValueType.boolean,
              basicValue: nestedValue,
            };
          }
        }
      }
    }
  }
}

// Helper function to determine configuration type and extract values
function extractConfigurationValues(config: CorporationsProjectsDetail["configuration"]) {
  // Check each possible configuration variant
  if ("capture_fw_complex" in config && config.capture_fw_complex) {
    return {
      type: "capture_fw_complex",
      data: config.capture_fw_complex,
    };
  }

  if ("damage_ship" in config && config.damage_ship) {
    return {
      type: "damage_ship",
      data: config.damage_ship,
    };
  }

  if ("defend_fw_complex" in config && config.defend_fw_complex) {
    return {
      type: "defend_fw_complex",
      data: config.defend_fw_complex,
    };
  }

  if ("deliver_item" in config && config.deliver_item) {
    return {
      type: "deliver_item",
      data: config.deliver_item,
    };
  }

  if ("destroy_npc" in config && config.destroy_npc) {
    return {
      type: "destroy_npc",
      data: config.destroy_npc,
    };
  }

  if ("destroy_ship" in config && config.destroy_ship) {
    return {
      type: "destroy_ship",
      data: config.destroy_ship,
    };
  }

  if ("earn_loyalty_point" in config && config.earn_loyalty_point) {
    return {
      type: "earn_loyalty_point",
      data: config.earn_loyalty_point,
    };
  }

  if ("lost_ship" in config && config.lost_ship) {
    return {
      type: "lost_ship",
      data: config.lost_ship,
    };
  }

  if ("manual" in config && config.manual) {
    return {
      type: "manual",
      data: config.manual,
    };
  }

  if ("manufacture_item" in config && config.manufacture_item) {
    return {
      type: "manufacture_item",
      data: config.manufacture_item,
    };
  }

  if ("mine_material" in config && config.mine_material) {
    return {
      type: "mine_material",
      data: config.mine_material,
    };
  }

  if ("remote_boost_shield" in config && config.remote_boost_shield) {
    return {
      type: "remote_boost_shield",
      data: config.remote_boost_shield,
    };
  }

  if ("remote_repair_armor" in config && config.remote_repair_armor) {
    return {
      type: "remote_repair_armor",
      data: config.remote_repair_armor,
    };
  }

  if ("salvage_wreck" in config && config.salvage_wreck) {
    return {
      type: "salvage_wreck",
      data: config.salvage_wreck,
    };
  }

  if ("scan_signature" in config && config.scan_signature) {
    return {
      type: "scan_signature",
      data: config.scan_signature,
    };
  }

  if ("ship_insurance" in config && config.ship_insurance) {
    return {
      type: "ship_insurance",
      data: config.ship_insurance,
    };
  }

  if ("unknown" in config && config.unknown) {
    return {
      type: "unknown",
      data: config.unknown,
    };
  }

  return {
    type: "none",
    data: null,
  };
}
